import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCopyToClipboard } from '@hooks/useCopyToClipboard';
import {
  ContentCopy,
  Download,
  ExpandLess,
  ExpandMore,
  Publish,
  Refresh,
  Visibility,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { getAuth0AccessTokenSilently } from '@store/auth0BaseQuery';
import { useSnackbar } from 'notistack';

import type {
  PatientResolution,
  SessionPatientMatchSchema,
} from '../store/hooks';
import {
  useCommitStagedResourcesMutation,
  useGetClassifySummaryQuery,
  useGetStagedResourcesSummaryQuery,
  useImportOperationTracking,
  useListAllClassifyConflictsQuery,
  useListAllSessionPatientMatchesQuery,
  useListSessionPatientsQuery,
  useListSourceProvidersQuery,
  useListStagedResourcesQuery,
} from '../store/hooks';

import { ArtifactViewerModal } from './artifact-viewer-modal';
import type { ReviewResourceRow } from './import-review-browser';
import { ImportReviewBrowser } from './import-review-browser';
import type {
  ImportVerdict,
  ReviewDecision,
  ReviewMatchCandidate,
  ReviewMatchResolution,
} from './import-review-types';
import { PatientSelectionPanel } from './patient-selection-panel';
import { ProviderMappingPanel } from './provider-mapping-panel';

const API_BASE_URL = import.meta.env.VITE_API_URL as string;

/** Filename of an artifact relative to its `_artifacts/` prefix. */
const artifactName = (key: string, prefix?: string): string =>
  prefix && key.startsWith(prefix) ? key.slice(prefix.length) : key;

const STATUSES = ['staged', 'invalid', 'committed', 'skipped'] as const;
const PER_PAGE = 25;
// Browse window for informational rows (new/unchanged/update). We fetch one page
// at the BE's max page size rather than an unbounded set; sessions larger than
// this are windowed for *browsing* only — every conflict is still fully loaded
// and resolvable (see the all-conflicts fetch), so nothing that blocks a commit
// is ever hidden. GDN-2198.
const BROWSER_PAGE_SIZE = 1000;

/** Best-effort human label for a converted payload (mirrors the BE). */
function reviewLabel(
  data: Record<string, unknown> | undefined,
  sourceKey: string,
  resourceType?: string,
): string {
  const d = data ?? {};
  const str = (k: string): string => (typeof d[k] === 'string' ? (d[k] as string) : '');
  const first = str('first_name');
  const last = str('last_name');
  if (first || last) return [first, last].filter(Boolean).join(' ');
  for (const k of ['display_name', 'name', 'title']) {
    const v = d[k];
    if (typeof v === 'string' && v.trim()) return v;
  }
  // Type-aware fallbacks for payloads with no name-ish field — a raw source
  // key tells the operator nothing (GDN-2211).
  if (resourceType === 'encounter') {
    const kind = (str('encounter_type') || 'encounter').replaceAll('_', ' ');
    const when = (str('start_time') || str('scheduled_for')).slice(0, 10);
    return [kind, when].filter(Boolean).join(' · ');
  }
  if (resourceType === 'patient_coverage' && str('payer_name')) {
    return str('payer_name');
  }
  return sourceKey;
}

const STATUS_COLOR: Record<
  string,
  'default' | 'info' | 'success' | 'warning' | 'error'
> = {
  staged: 'info',
  invalid: 'error',
  committed: 'success',
  skipped: 'warning',
};

interface StagedResourcesSectionProps {
  organizationId: string;
  sessionId: string;
  /** Offer the Commit action (the v2 Data Import batch detail enables it). */
  showCommit?: boolean;
}

/**
 * Displays the staged `import_resources` for an import session — a summary of
 * counts by resource type × status, plus a filterable, paginated table of the
 * staged resource envelopes (status, source key, validation errors, and the
 * converted resource data). The conversion that produces these runs outside
 * this repo. With `showCommit`, also offers the commit step: staged rows
 * become real records via an async operation polled to completion.
 */
export function StagedResourcesSection({
  organizationId,
  sessionId,
  showCommit = false,
}: StagedResourcesSectionProps) {
  const [page, setPage] = useState(1);
  const [resourceType, setResourceType] = useState('');
  const [status, setStatus] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [committing, setCommitting] = useState(false);
  // Types the operator explicitly UNchecked. Empty = commit all (the default),
  // and any new type appears selected — no init-from-async needed.
  const [deselected, setDeselected] = useState<Set<string>>(new Set());
  // source provider key -> platform user id, supplied by the operator.
  const [providerMapping, setProviderMapping] = useState<Record<string, string>>(
    {},
  );
  // Patient scope. Default 'all' commits every staged patient (no patient_keys
  // sent); 'specific' reveals the picker and commits only the checked patients
  // (inclusion model — empty until the operator chooses).
  const [patientScope, setPatientScope] = useState<'all' | 'specific'>('all');
  const [selectedPatients, setSelectedPatients] = useState<Set<string>>(
    new Set(),
  );
  // Operator match decisions, keyed by patient source key. An ambiguous patient
  // included in the commit must be resolved here before commit is allowed.
  const [resolutions, setResolutions] = useState<Map<string, PatientResolution>>(
    new Map(),
  );
  const [viewerKey, setViewerKey] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const copy = useCopyToClipboard();

  const {
    data: summary,
    isLoading: summaryLoading,
    refetch: refetchSummary,
  } = useGetStagedResourcesSummaryQuery({ organizationId, sessionId });

  const {
    data: list,
    isLoading: listLoading,
    isFetching,
    refetch: refetchList,
  } = useListStagedResourcesQuery({
    organizationId,
    sessionId,
    page,
    perPage: PER_PAGE,
    resourceType: resourceType || undefined,
    status: status || undefined,
  });

  // Classify review (GDN-2178): a verdict-aware browser over every staged row.
  const [flagged, setFlagged] = useState<Set<string>>(new Set());
  // Operator decisions for conflicting rows, keyed by import_resource_id. Sent
  // as conflict_resolutions; every in-scope conflict must be decided to commit.
  const [decisions, setDecisions] = useState<Record<string, ReviewDecision>>(
    {},
  );
  const { data: classifySummary } = useGetClassifySummaryQuery({
    organizationId,
    sessionId,
  });
  const hasClassifyVerdicts = (classifySummary?.total_records ?? 0) > 0;
  // The field-merge detail (competing + fills) carries PHI — fetch it only when
  // the session has matched-with-changes rows (conflicts or updates) to enrich.
  const hasMatchedChanges =
    (classifySummary?.totals?.conflict ?? 0) > 0 ||
    (classifySummary?.totals?.update ?? 0) > 0;
  const { data: conflicts, isLoading: conflictsLoading } =
    useListAllClassifyConflictsQuery(
      // Fetch EVERY conflict in the session (paged to completion), not just the
      // first browse window. An undecided conflict is the only thing that blocks
      // a commit, so all of them must be shown and resolvable no matter how large
      // the import is; a conflict left off a page would block the commit with no
      // way to decide it. GDN-2198.
      { organizationId, sessionId },
      { skip: !hasMatchedChanges },
    );
  // Informational rows (new/unchanged/update) for browsing, windowed at the BE's
  // max page size. These don't block a commit, so we don't fetch an unbounded
  // set; conflicts beyond this window are still surfaced (see reviewRows below).
  const { data: allRowsData } = useListStagedResourcesQuery(
    { organizationId, sessionId, page: 1, perPage: BROWSER_PAGE_SIZE },
    { skip: !hasClassifyVerdicts },
  );

  const browserTotals = {
    new: 0,
    unchanged: 0,
    update: 0,
    conflict: 0,
    ambiguous: 0,
    unprocessable: 0,
    ...(classifySummary?.totals ?? {}),
  };

  // Informational rows past the browse window (new/unchanged/update records that
  // don't need individual review). This is a browsing limit we surface honestly
  // — NOT a correctness gap, because every conflict is fully loaded below.
  const loadedRowCount = allRowsData?.items?.length ?? 0;
  const totalRowCount = allRowsData?.total ?? loadedRowCount;
  const browserWindowed = totalRowCount > loadedRowCount;
  // Whole-session conflict total from the summary (independent of any page).
  const totalConflictCount = classifySummary?.totals?.conflict ?? 0;
  // Pathological guard: the conflict fetch pages to completion, so this is only
  // true for an absurdly large conflict set (beyond the fetch's page backstop).
  // Gate the commit if we somehow couldn't load them all rather than miss one.
  const conflictsIncomplete =
    !!conflicts && (conflicts.total ?? 0) > (conflicts.items?.length ?? 0);

  // Per-patient match outcomes (staged patients only). Feeds the resolution UI
  // in the patient picker AND the ambiguous rows in the review browser — the
  // match feed carries candidate names/dob the staged row's persisted candidate
  // ids don't. Fetched to completion (paged past the 1000 cap) so a large batch
  // never silently hides an ambiguous match; matchesIncomplete still gates as a
  // backstop for the pathological case. GDN-2198 / GDN-2212.
  const { data: matchData, isLoading: matchesLoading } =
    useListAllSessionPatientMatchesQuery(
      { organizationId, sessionId },
      { skip: !showCommit },
    );
  const matchByKey = useMemo(
    () =>
      new Map<string, SessionPatientMatchSchema>(
        (matchData?.items ?? []).map(m => [m.patient_key, m]),
      ),
    [matchData],
  );
  const matchesIncomplete =
    !!matchData && (matchData.total ?? 0) > (matchData.items?.length ?? 0);
  const setResolution = useCallback(
    (key: string, resolution: PatientResolution | null) =>
      setResolutions(prev => {
        const next = new Map(prev);
        if (resolution) next.set(key, resolution);
        else next.delete(key);
        return next;
      }),
    [],
  );

  const reviewRows = useMemo<ReviewResourceRow[]>(() => {
    const items = allRowsData?.items ?? [];
    const conflictItems = conflicts?.items ?? [];
    const detailById = new Map(
      conflictItems.map((c) => [c.import_resource_id, c]),
    );
    const toDiff = (d: {
      field: string;
      existing?: string | null;
      incoming?: string | null;
    }) => ({ field: d.field, existing: d.existing ?? '', incoming: d.incoming ?? '' });
    // Candidates for an ambiguous patient row come from the match feed (which
    // carries names/dob for display); the staged row's persisted
    // match_candidates (ids + confidence only) are the fallback.
    const candidatesFor = (
      sourceKey: string,
      persisted: { patient_id: string; confidence: number; reason: string }[] | undefined,
    ): ReviewMatchCandidate[] | undefined => {
      const fromMatch = matchByKey.get(sourceKey)?.candidates;
      if (fromMatch && fromMatch.length > 0) {
        return fromMatch.map((c) => ({
          patientId: c.patient_id,
          label:
            [c.name, c.dob].filter(Boolean).join(' · ') || c.patient_id,
          confidence: c.confidence,
          reason: c.reason,
        }));
      }
      return persisted?.map((c) => ({
        patientId: c.patient_id,
        label: c.patient_id,
        confidence: c.confidence,
        reason: c.reason,
      }));
    };
    // Patient labels by source key, so child rows (encounters) can say whose
    // record they are — every staged row is already in this list.
    const patientNameByKey = new Map(
      items
        .filter((i) => i.resource_type === 'patient')
        .map((i) => [i.source_key, reviewLabel(i.data, i.source_key, 'patient')]),
    );
    const windowedRows = items.flatMap((item) => {
      const classification = (item as { classification?: string | null })
        .classification;
      const verdict: ImportVerdict | undefined =
        item.status === 'invalid'
          ? 'unprocessable'
          : (classification as ImportVerdict | undefined) ?? undefined;
      if (!verdict) return [];
      const detail = detailById.get(item.import_resource_id);
      let label =
        detail?.title ??
        reviewLabel(item.data, item.source_key, item.resource_type);
      if (item.resource_type === 'encounter') {
        const patientName = patientNameByKey.get(item.refs?.patient ?? '');
        if (patientName && patientName !== (item.refs?.patient ?? '')) {
          label = `${patientName} · ${label}`;
        }
      }
      const row: ReviewResourceRow = {
        id: item.import_resource_id,
        resourceType: item.resource_type,
        label,
        // The sublabel exists to carry provenance; when the label already IS
        // the source key it would just print the same string twice.
        sublabel: label === item.source_key ? undefined : item.source_key,
        verdict,
        matchedSummary: detail?.matched_summary ?? undefined,
        competing: detail?.competing?.map(toDiff),
        autoMerge: detail?.auto_merge?.map(toDiff),
        matchCandidates:
          verdict === 'ambiguous'
            ? candidatesFor(
                item.source_key,
                (
                  item as {
                    match_candidates?: {
                      patient_id: string;
                      confidence: number;
                      reason: string;
                    }[];
                  }
                ).match_candidates,
              )
            : undefined,
        data: item.data,
        flagged: flagged.has(item.import_resource_id),
      };
      return [row];
    });
    // Surface every conflict, even ones whose staged row fell outside the browse
    // window: synthesize a row from the conflict detail (which carries the label,
    // matched summary, and competing/auto-merge fields needed to decide it). The
    // raw payload isn't in the conflict detail, so `data` is left off for these.
    const shownIds = new Set(windowedRows.map((r) => r.id));
    const extraConflictRows = conflictItems
      .filter((c) => !shownIds.has(c.import_resource_id))
      .map<ReviewResourceRow>((c) => ({
        id: c.import_resource_id,
        resourceType: c.resource_type,
        label: c.title ?? c.source_key,
        sublabel: c.title ? c.source_key : undefined,
        verdict: 'conflict',
        matchedSummary: c.matched_summary ?? undefined,
        competing: c.competing?.map(toDiff),
        autoMerge: c.auto_merge?.map(toDiff),
        flagged: flagged.has(c.import_resource_id),
      }));
    // Same for ambiguous patients: the match feed is paged to completion, so
    // synthesize a row for any ambiguous patient whose staged row fell outside
    // the browse window (the feed carries name/dob for the label and the
    // candidate set). The row id is the patient source key — the resolution
    // plumbing resolves it back to the same `resolutions` entry.
    const shownPatientKeys = new Set(
      items
        .filter((i) => i.resource_type === 'patient')
        .map((i) => i.source_key),
    );
    const extraAmbiguousRows = [...matchByKey.entries()]
      .filter(
        ([key, m]) => m.status === 'ambiguous' && !shownPatientKeys.has(key),
      )
      .map<ReviewResourceRow>(([key, m]) => {
        const label = [m.name, m.dob].filter(Boolean).join(' · ') || key;
        return {
          id: key,
          resourceType: 'patient',
          label,
          sublabel: label === key ? undefined : key,
          verdict: 'ambiguous',
          matchCandidates: candidatesFor(key, undefined),
          flagged: flagged.has(key),
        };
      });
    return [...windowedRows, ...extraConflictRows, ...extraAmbiguousRows];
  }, [allRowsData, conflicts, flagged, matchByKey]);

  // Ambiguous patient rows carry their resolution through the same
  // `resolutions` map (keyed by patient source key) the patient picker uses —
  // one source of truth, both UIs stay in sync. The browser speaks row ids, so
  // adapt both directions. Beyond-window ambiguous rows are synthesized with
  // the source key AS the row id, so those map to themselves.
  const patientKeyByRowId = useMemo(() => {
    const map = new Map(
      (allRowsData?.items ?? [])
        .filter((i) => i.resource_type === 'patient')
        .map((i) => [i.import_resource_id, i.source_key]),
    );
    for (const [key, m] of matchByKey) {
      if (m.status === 'ambiguous' && !map.has(key)) map.set(key, key);
    }
    return map;
  }, [allRowsData, matchByKey]);
  const browserMatchResolutions = useMemo(() => {
    const out: Record<string, ReviewMatchResolution> = {};
    for (const [rowId, key] of patientKeyByRowId) {
      const r = resolutions.get(key);
      if (!r) continue;
      out[rowId] =
        r.action === 'link' && r.target_patient_id
          ? { action: 'link', targetPatientId: r.target_patient_id }
          : { action: 'create' };
    }
    return out;
  }, [patientKeyByRowId, resolutions]);
  const handleBrowserMatchResolution = useCallback(
    (rowId: string, resolution: ReviewMatchResolution | null) => {
      const key = patientKeyByRowId.get(rowId);
      if (!key) return;
      setResolution(
        key,
        resolution == null
          ? null
          : resolution.action === 'link'
            ? { action: 'link', target_patient_id: resolution.targetPatientId }
            : { action: 'create' },
      );
    },
    [patientKeyByRowId, setResolution],
  );

  const countsByType = summary?.counts_by_resource_type ?? {};
  const resourceTypes = Object.keys(countsByType).sort();
  const items = list?.items ?? [];
  const total = list?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  const refresh = () => {
    void refetchSummary();
    void refetchList();
  };

  // The converter provenance recorded at staging time (GDN-1978): the
  // _artifacts/ prefix, the artifact object keys, and the convert.py hash.
  const artifactsPrefix = summary?.artifacts_prefix ?? undefined;
  const artifactKeys = summary?.artifact_keys ?? [];
  const converterSha256 = summary?.converter_sha256 ?? undefined;
  const hasProvenance = Boolean(
    artifactsPrefix || converterSha256 || artifactKeys.length,
  );

  // If the loader stashed `_artifacts/provider_mapping.json` with the batch, read
  // it so the provider-mapping step can offer a one-click pre-fill instead of
  // resolving each provider by hand. Best-effort: any failure just hides the
  // button. Mirrors ArtifactViewerModal's content fetch.
  const mappingArtifactKey = useMemo(
    () =>
      (summary?.artifact_keys ?? []).find(
        k => artifactName(k, artifactsPrefix) === 'provider_mapping.json',
      ),
    [summary?.artifact_keys, artifactsPrefix],
  );
  const [suggestedMapping, setSuggestedMapping] = useState<
    Record<string, string> | undefined
  >(undefined);

  useEffect(() => {
    if (!showCommit || !mappingArtifactKey) {
      setSuggestedMapping(undefined);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const token = await getAuth0AccessTokenSilently();
        const params = new URLSearchParams({
          organization_id: organizationId,
          key: mappingArtifactKey,
        });
        const res = await fetch(
          `${API_BASE_URL}/v2/admin/data-migration/sessions/${sessionId}/artifacts/content?${params}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!res.ok) return;
        const data = (await res.json()) as { text?: string };
        const parsed: unknown = data.text ? JSON.parse(data.text) : undefined;
        if (
          !cancelled &&
          parsed &&
          typeof parsed === 'object' &&
          !Array.isArray(parsed)
        ) {
          setSuggestedMapping(parsed as Record<string, string>);
        }
      } catch {
        // best-effort; no pre-fill button if the artifact can't be read/parsed
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showCommit, mappingArtifactKey, organizationId, sessionId]);

  // Open a single artifact via a short-lived presigned URL. The backend only
  // signs keys recorded on this session, so an arbitrary key can't be fetched.
  // Fetched fresh each time (not via cached RTK query) since the URL expires.
  const openArtifact = async (key: string) => {
    try {
      const token = await getAuth0AccessTokenSilently();
      const params = new URLSearchParams({
        organization_id: organizationId,
        key,
      });
      const res = await fetch(
        `${API_BASE_URL}/v2/admin/data-migration/sessions/${sessionId}/artifacts/download?${params}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) {
        throw new Error(`Download failed (${res.status})`);
      }
      const data = (await res.json()) as { url: string };
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch {
      enqueueSnackbar('Could not open artifact.', { variant: 'error' });
    }
  };

  // A type is committable in a run only if it has staged rows. Selection
  // defaults to all such types; the operator can uncheck to commit a subset
  // (e.g. patients first, then their children).
  const selectableTypes = resourceTypes.filter(
    rt => (countsByType[rt]?.staged ?? 0) > 0,
  );
  const selectedTypes = selectableTypes.filter(rt => !deselected.has(rt));
  const selectedStagedCount = selectedTypes.reduce(
    (acc, rt) => acc + (countsByType[rt]?.staged ?? 0),
    0,
  );
  const allSelected =
    selectableTypes.length > 0 &&
    selectedTypes.length === selectableTypes.length;

  const toggleType = (rt: string) =>
    setDeselected(prev => {
      const next = new Set(prev);
      if (next.has(rt)) next.delete(rt);
      else next.add(rt);
      return next;
    });

  const toggleAll = () =>
    setDeselected(allSelected ? new Set(selectableTypes) : new Set());

  // A child commits only once its patient is committed; warn if patients are
  // still staged but excluded from this selection.
  const patientsStaged = (countsByType['patient']?.staged ?? 0) > 0;
  const childSelectedWithoutPatients =
    patientsStaged &&
    !selectedTypes.includes('patient') &&
    selectedTypes.some(rt => rt !== 'patient');

  // Provider mapping gate: encounters commit owned by their source provider,
  // so a required (encounter-referenced) provider must be mapped before the
  // commit can include encounters — else those rows block server-side.
  const { data: sourceProviders } = useListSourceProvidersQuery(
    { organizationId, sessionId },
    { skip: !showCommit },
  );
  const providers = sourceProviders?.items ?? [];
  const encounterSelected = selectedTypes.includes('encounter');
  const unmappedRequired = encounterSelected
    ? providers.filter(p => p.required && !providerMapping[p.provider_key])
    : [];
  const providerGated = unmappedRequired.length > 0;

  // Commit by patient: choose which patients (+ their dependency closure) to
  // commit. Defaults to all selected; a subset sends patient_keys.
  const { data: patientData, isLoading: patientsLoading } =
    useListSessionPatientsQuery(
      { organizationId, sessionId },
      { skip: !showCommit },
    );
  const patients = patientData?.items ?? [];
  const committingAllPatients = patientScope === 'all';
  const selectedPatientKeys = patients
    .map(p => p.patient_key)
    .filter(k => selectedPatients.has(k));

  // An ambiguous patient that's actually being committed must be resolved.
  // 'all' includes every patient; 'specific' only the checked ones. Resolution
  // only matters when the patient type is part of the commit.
  const includedPatientKeys = committingAllPatients
    ? patients.map(p => p.patient_key)
    : selectedPatientKeys;
  const unresolvedAmbiguous = selectedTypes.includes('patient')
    ? includedPatientKeys.filter(
        k => matchByKey.get(k)?.status === 'ambiguous' && !resolutions.has(k),
      )
    : [];
  // Every conflicting row of a type being committed must carry a decision: the
  // backend blocks the run otherwise (no auto-escalation). Scope mirrors the
  // backend gate — by resource type, not the patient picker. Computed from the
  // fully-loaded conflict set (paged to completion), so it's complete regardless
  // of the browse window; conflictsPending (below) covers the load-in-flight gap.
  const unresolvedConflicts = (conflicts?.items ?? []).filter(
    (c) =>
      selectedTypes.includes(c.resource_type) &&
      !decisions[c.import_resource_id],
  );
  // Until the conflict fetch resolves we can't see the conflicts yet, so a
  // premature commit would be rejected server-side. Gate while it's in flight.
  const conflictsPending = hasMatchedChanges && (conflictsLoading || !conflicts);

  // Gate while match results are still loading: until they resolve, matchByKey
  // is empty so unresolvedAmbiguous can't see ambiguous patients yet.
  const commitGated =
    providerGated ||
    matchesLoading ||
    unresolvedAmbiguous.length > 0 ||
    matchesIncomplete ||
    conflictsPending ||
    conflictsIncomplete ||
    unresolvedConflicts.length > 0;

  // The commit count must match what actually commits. `selectedStagedCount`
  // is purely type-based and ignores the patient picker, so with a patient
  // subset it overstates the run by counting every patient's rows. Re-scope it
  // to the selected patients' closure: each staged patient row counts once
  // (the picker's per-patient `counts` excludes the patient row itself), child
  // rows come from those per-type counts. Scope 'all' sends no patient_keys, so
  // the full type-based total is exactly right.
  const isFullCommit = allSelected && committingAllPatients;
  const scopedStagedCount = committingAllPatients
    ? selectedStagedCount
    : selectedPatientKeys.reduce((runTotal, key) => {
        const summary = patients.find(p => p.patient_key === key);
        if (!summary) return runTotal;
        return (
          runTotal +
          selectedTypes.reduce((perPatient, rt) => {
            if (rt === 'patient') {
              return perPatient + (summary.patient_staged ? 1 : 0);
            }
            return perPatient + (summary.counts?.[rt] ?? 0);
          }, 0)
        );
      }, 0);

  const togglePatient = (key: string) =>
    setSelectedPatients(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  // Select-all within 'specific' mode: check every patient, or clear if all are
  // already checked.
  const allPatientsChecked =
    patients.length > 0 && selectedPatientKeys.length === patients.length;
  const toggleAllPatients = () =>
    setSelectedPatients(
      allPatientsChecked ? new Set() : new Set(patients.map(p => p.patient_key)),
    );
  const changePatientScope = (scope: 'all' | 'specific') => {
    setPatientScope(scope);
    if (scope === 'all') setSelectedPatients(new Set());
  };

  const [commitStagedResources] = useCommitStagedResourcesMutation();
  const operationTracking = useImportOperationTracking({
    sessionId,
    onOperationComplete: operation => {
      setCommitting(false);
      // Reset selections so the next run defaults back to "all staged":
      // every type, no carried-over provider mapping, patient scope back to all.
      setDeselected(new Set());
      setProviderMapping({});
      setPatientScope('all');
      setSelectedPatients(new Set());
      setResolutions(new Map());
      setDecisions({});
      setFlagged(new Set());
      enqueueSnackbar(
        `Commit complete: ${operation.progress?.imported_count ?? 0} committed, ` +
          `${operation.progress?.failed_count ?? 0} failed, ` +
          `${operation.progress?.skipped_count ?? 0} skipped.`,
        { variant: 'success' },
      );
      refresh();
    },
    onOperationError: operation => {
      setCommitting(false);
      enqueueSnackbar(`Commit failed: ${operation.error ?? 'Unknown error'}`, {
        variant: 'error',
      });
      refresh();
    },
  });

  const handleCommit = async () => {
    setConfirmOpen(false);
    setCommitting(true);
    try {
      const result = await commitStagedResources({
        sessionId,
        commitStagedResourcesRequest: {
          organization_id: organizationId,
          // Send the explicit subset; all-selected == commit everything.
          resource_types: selectedTypes,
          // Source provider -> platform user; encounters land owned by the
          // mapped clinician. Unmapped required providers are gated before here.
          provider_mapping: providerMapping,
          // Scope to the chosen patients (+ their closure); omit in 'all' mode
          // so the backend commits every patient.
          patient_keys: committingAllPatients ? undefined : selectedPatientKeys,
          // Operator match decisions; omit when none so auto-matching applies.
          patient_resolutions:
            resolutions.size > 0
              ? Object.fromEntries(resolutions)
              : undefined,
          // Operator conflict decisions, keyed by import_resource_id; omit when
          // none. The commit button is gated until every in-scope conflict is
          // decided, so by here these cover them.
          conflict_resolutions:
            Object.keys(decisions).length > 0 ? decisions : undefined,
        },
      }).unwrap();
      if (result.operation_id) {
        operationTracking.startTracking(result.operation_id);
      } else {
        setCommitting(false);
        refresh();
      }
    } catch (error: unknown) {
      setCommitting(false);
      const message =
        (error as { data?: { message?: string; error_code?: string } })?.data
          ?.message ?? 'Failed to dispatch commit.';
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title="Staged Resources"
        subheader={
          summary
            ? `Session ${summary.status} · ${total} resource(s)` +
              (summary.s3_prefix ? ` · ${summary.s3_prefix}` : '')
            : undefined
        }
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {showCommit && (
              <Tooltip
                title={
                  matchesLoading || conflictsPending
                    ? 'Loading review results…'
                    : matchesIncomplete
                      ? 'Too many patients to load match results at once — commit is disabled to avoid missing an ambiguous match'
                      : conflictsIncomplete
                        ? 'Too many conflicts to load at once — commit is disabled to avoid missing a required decision'
                        : unresolvedAmbiguous.length > 0
                          ? `Resolve ${unresolvedAmbiguous.length} ambiguous patient match(es) before committing`
                          : unresolvedConflicts.length > 0
                            ? `Resolve ${unresolvedConflicts.length} conflict(s) before committing`
                            : providerGated
                              ? 'Map all providers with encounters before committing'
                              : ''
                }
              >
                <span>
                  <Button
                    variant="contained"
                    startIcon={
                      committing ? <CircularProgress size={16} /> : <Publish />
                    }
                    disabled={
                      committing || scopedStagedCount === 0 || commitGated
                    }
                    onClick={() => setConfirmOpen(true)}
                  >
                    {committing
                      ? 'Committing…'
                      : isFullCommit
                        ? `Commit staged (${scopedStagedCount})`
                        : `Commit selected (${scopedStagedCount})`}
                  </Button>
                </span>
              </Tooltip>
            )}
            <Tooltip title="Batch already converted — adding files is locked. Start a new batch instead.">
              <span>
                <Button variant="outlined" disabled>
                  Add files
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton
                onClick={refresh}
                aria-label="Refresh staged resources"
              >
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
        }
      />
      <CardContent>
        {committing && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Commit running in the background — staged resources are being
            written to the live tables. This view refreshes when it finishes.
          </Alert>
        )}
        {hasClassifyVerdicts && classifySummary && (
          <Box sx={{ mb: 3 }}>
            {(conflictsIncomplete || matchesIncomplete) && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                This import is unusually large and some conflict or patient-match
                data couldn&apos;t be fully loaded for review. Committing is
                disabled to avoid missing a required decision — contact support.
              </Alert>
            )}
            {browserWindowed && !conflictsIncomplete && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Showing the first {loadedRowCount.toLocaleString()} of{' '}
                {totalRowCount.toLocaleString()} records for browsing. The rest
                are new / unchanged / updated records that don&apos;t need
                individual review — they commit normally.{' '}
                {/* Speak only to conflicts — other gates (ambiguous patient
                    matches, unmapped providers) may still block the commit;
                    the commit button's tooltip owns the readiness story. */}
                {totalConflictCount > 0
                  ? `All ${totalConflictCount.toLocaleString()} conflict(s) are loaded below and must be resolved before committing.`
                  : 'This import has no conflicts to review.'}
              </Alert>
            )}
            <ImportReviewBrowser
              sourceLabel={classifySummary.source_label ?? undefined}
              totalRecords={classifySummary.total_records ?? reviewRows.length}
              totals={browserTotals}
              rows={reviewRows}
              decisions={decisions}
              onDecisionChange={(id, decision) =>
                setDecisions((prev) => {
                  if (decision) return { ...prev, [id]: decision };
                  const { [id]: _removed, ...rest } = prev;
                  return rest;
                })
              }
              matchResolutions={browserMatchResolutions}
              onMatchResolutionChange={handleBrowserMatchResolution}
              onFlagChange={(id, isFlagged) =>
                setFlagged((prev) => {
                  const next = new Set(prev);
                  if (isFlagged) next.add(id);
                  else next.delete(id);
                  return next;
                })
              }
              showCommitBar={false}
              provenance={
                hasProvenance ? (
                  <Box sx={{ fontSize: 13 }}>
                    {converterSha256 && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          fontFamily: 'monospace',
                          wordBreak: 'break-all',
                          mb: 0.5,
                        }}
                      >
                        convert.py SHA-256 · {converterSha256}
                      </Typography>
                    )}
                    {artifactKeys.map((key) => (
                      <Typography
                        key={key}
                        variant="caption"
                        onClick={() => setViewerKey(key)}
                        sx={{
                          display: 'block',
                          color: 'primary.main',
                          cursor: 'pointer',
                          fontFamily: 'monospace',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        {artifactName(key, artifactsPrefix)}
                      </Typography>
                    ))}
                  </Box>
                ) : undefined
              }
            />
          </Box>
        )}
        {!hasClassifyVerdicts && hasProvenance && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Converter provenance
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 1.5 }}
            >
              How this batch was converted, captured when it was staged.
            </Typography>
            {converterSha256 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: artifactsPrefix || artifactKeys.length ? 1 : 0,
                }}
              >
                <Typography variant="body2" sx={{ minWidth: 120 }}>
                  convert.py SHA-256
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: 12,
                    wordBreak: 'break-all',
                    flex: 1,
                  }}
                >
                  {converterSha256}
                </Typography>
                <Tooltip title="Copy SHA-256">
                  <IconButton
                    size="small"
                    aria-label="Copy converter SHA-256"
                    onClick={() => copy(converterSha256, 'SHA-256')}
                  >
                    <ContentCopy fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            {artifactsPrefix && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: artifactKeys.length ? 1 : 0,
                }}
              >
                <Typography variant="body2" sx={{ minWidth: 120 }}>
                  Artifacts prefix
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: 12,
                    wordBreak: 'break-all',
                    flex: 1,
                  }}
                >
                  {artifactsPrefix}
                </Typography>
                <Tooltip title="Copy prefix">
                  <IconButton
                    size="small"
                    aria-label="Copy artifacts prefix"
                    onClick={() => copy(artifactsPrefix, 'Prefix')}
                  >
                    <ContentCopy fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            {artifactKeys.length > 0 && (
              <Box>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Artifacts
                </Typography>
                {artifactKeys.map((key) => (
                  <Box
                    key={key}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      py: 0.25,
                    }}
                  >
                    <Typography
                      variant="body2"
                      onClick={() => setViewerKey(key)}
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: 12,
                        wordBreak: 'break-all',
                        flex: 1,
                        color: 'primary.main',
                        cursor: 'pointer',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      {artifactName(key, artifactsPrefix)}
                    </Typography>
                    <Tooltip title="View">
                      <IconButton
                        size="small"
                        aria-label={`View artifact ${key}`}
                        onClick={() => setViewerKey(key)}
                      >
                        <Visibility fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download">
                      <IconButton
                        size="small"
                        aria-label={`Download artifact ${key}`}
                        onClick={() => void openArtifact(key)}
                      >
                        <Download fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
        {summaryLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : resourceTypes.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No staged resources for this session yet.
          </Typography>
        ) : (
          <Table size="small" sx={{ mb: 3 }}>
            <TableHead>
              <TableRow>
                {showCommit && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={selectedTypes.length > 0 && !allSelected}
                      disabled={selectableTypes.length === 0}
                      onChange={toggleAll}
                      slotProps={{
                        input: { 'aria-label': 'Select all types to commit' },
                      }}
                    />
                  </TableCell>
                )}
                <TableCell>Resource type</TableCell>
                {STATUSES.map(s => (
                  <TableCell key={s} align="right">
                    {s}
                  </TableCell>
                ))}
                <TableCell align="right">total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {resourceTypes.map(rt => {
                const row = countsByType[rt] ?? {};
                const rowTotal = Object.values(row).reduce((a, b) => a + b, 0);
                const committable = (row.staged ?? 0) > 0;
                return (
                  <TableRow
                    key={rt}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      setResourceType(rt);
                      setPage(1);
                    }}
                  >
                    {showCommit && (
                      <TableCell
                        padding="checkbox"
                        onClick={e => e.stopPropagation()}
                      >
                        <Checkbox
                          size="small"
                          checked={selectedTypes.includes(rt)}
                          disabled={!committable}
                          onChange={() => toggleType(rt)}
                          slotProps={{ input: { 'aria-label': `Commit ${rt}` } }}
                        />
                      </TableCell>
                    )}
                    <TableCell>{rt}</TableCell>
                    {STATUSES.map(s => (
                      <TableCell key={s} align="right">
                        {row[s] ?? 0}
                      </TableCell>
                    ))}
                    <TableCell align="right">
                      <strong>{rowTotal}</strong>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {showCommit && childSelectedWithoutPatients && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Some selected types depend on patients, but patients are still
            staged and not selected. Commit patients first, or include them —
            otherwise their children will block as unresolved.
          </Alert>
        )}

        {showCommit && (
          <ProviderMappingPanel
            organizationId={organizationId}
            providers={providers}
            value={providerMapping}
            onChange={setProviderMapping}
            encounterSelected={encounterSelected}
            suggestedMapping={suggestedMapping}
          />
        )}

        {showCommit && (
          <PatientSelectionPanel
            patients={patients}
            scope={patientScope}
            onScopeChange={changePatientScope}
            selected={selectedPatients}
            allChecked={allPatientsChecked}
            onToggle={togglePatient}
            onToggleAll={toggleAllPatients}
            isLoading={patientsLoading}
            matchByKey={matchByKey}
            resolutions={resolutions}
            onResolve={setResolution}
          />
        )}

        {!hasClassifyVerdicts && (
          <>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Resource type</InputLabel>
            <Select
              label="Resource type"
              value={resourceType}
              onChange={e => {
                setResourceType(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="">All</MenuItem>
              {resourceTypes.map(rt => (
                <MenuItem key={rt} value={rt}>
                  {rt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={status}
              onChange={e => {
                setStatus(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="">All</MenuItem>
              {STATUSES.map(s => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {isFetching && <CircularProgress size={18} />}
        </Box>

        {listLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No resources match the current filters.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Resource type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Committed ID</TableCell>
                <TableCell>Source key</TableCell>
                <TableCell align="right">Errors</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map(item => {
                const isOpen = expanded === item.import_resource_id;
                const errs = item.validation_errors ?? [];
                const commitErrs = item.commit_errors ?? [];
                return (
                  <Fragment key={item.import_resource_id}>
                    <TableRow hover>
                      <TableCell>
                        <IconButton
                          size="small"
                          aria-label="Toggle details"
                          onClick={() =>
                            setExpanded(
                              isOpen ? null : item.import_resource_id,
                            )
                          }
                        >
                          {isOpen ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{item.resource_type}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={item.status}
                          color={STATUS_COLOR[item.status] ?? 'default'}
                        />
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                        {item.committed_resource_id ? (
                          <Link
                            to={`/admin-v2/resources/${item.resource_type}/${item.committed_resource_id}?organization_id=${organizationId}`}
                          >
                            {item.committed_resource_id}
                          </Link>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: 12,
                          wordBreak: 'break-all',
                        }}
                      >
                        {item.source_key}
                      </TableCell>
                      <TableCell align="right">
                        {errs.length > 0 || commitErrs.length > 0 ? (
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 0.5,
                              justifyContent: 'flex-end',
                            }}
                          >
                            {errs.length > 0 && (
                              <Chip
                                size="small"
                                color="error"
                                label={`${errs.length} validation`}
                              />
                            )}
                            {commitErrs.length > 0 && (
                              <Chip
                                size="small"
                                color="warning"
                                label={`${commitErrs.length} commit`}
                              />
                            )}
                          </Box>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        sx={{ py: 0, border: isOpen ? undefined : 0 }}
                      >
                        <Collapse in={isOpen} unmountOnExit>
                          <Box sx={{ p: 2 }}>
                            {errs.length > 0 && (
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" color="error">
                                  Validation errors
                                </Typography>
                                <pre
                                  style={{
                                    margin: 0,
                                    fontSize: 12,
                                    whiteSpace: 'pre-wrap',
                                  }}
                                >
                                  {JSON.stringify(errs, null, 2)}
                                </pre>
                              </Box>
                            )}
                            {commitErrs.length > 0 && (
                              <Box sx={{ mb: 2 }}>
                                <Typography
                                  variant="subtitle2"
                                  color="warning.main"
                                >
                                  Commit errors
                                </Typography>
                                <pre
                                  style={{
                                    margin: 0,
                                    fontSize: 12,
                                    whiteSpace: 'pre-wrap',
                                  }}
                                >
                                  {JSON.stringify(commitErrs, null, 2)}
                                </pre>
                              </Box>
                            )}
                            <Typography variant="subtitle2">Data</Typography>
                            <pre
                              style={{
                                margin: 0,
                                fontSize: 12,
                                whiteSpace: 'pre-wrap',
                                maxHeight: 320,
                                overflow: 'auto',
                              }}
                            >
                              {JSON.stringify(item.data ?? {}, null, 2)}
                            </pre>
                            {item.refs &&
                              Object.keys(item.refs).length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="subtitle2">
                                    Refs
                                  </Typography>
                                  <pre style={{ margin: 0, fontSize: 12 }}>
                                    {JSON.stringify(item.refs, null, 2)}
                                  </pre>
                                </Box>
                              )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}

        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_e, p) => setPage(p)}
              size="small"
            />
          </Box>
        )}
          </>
        )}
      </CardContent>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>
          {isFullCommit ? 'Commit staged resources?' : 'Commit selected?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This writes {scopedStagedCount} staged resource
            {scopedStagedCount === 1 ? '' : 's'}
            {allSelected ? '' : ` (${selectedTypes.join(', ')})`}
            {committingAllPatients
              ? ''
              : ` for ${selectedPatientKeys.length} selected patient${
                  selectedPatientKeys.length === 1 ? '' : 's'
                }`}{' '}
            into the live tables for this organization. Rows that fail are
            marked with commit errors and the run continues; already-committed
            rows are skipped, so re-running is safe.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void handleCommit()}>
            Commit
          </Button>
        </DialogActions>
      </Dialog>

      <ArtifactViewerModal
        open={viewerKey !== null}
        onClose={() => setViewerKey(null)}
        organizationId={organizationId}
        sessionId={sessionId}
        artifactKey={viewerKey}
        displayName={
          viewerKey ? artifactName(viewerKey, artifactsPrefix) : ''
        }
        onDownload={(key) => void openArtifact(key)}
      />
    </Card>
  );
}
